import React from 'react';
import { ExternalLink, ShieldCheck } from 'lucide-react';

interface HostingerPartnerBadgeProps {
  className?: string;
  variant?: 'card' | 'inline' | 'compact' | 'footer';
}

export const HOSTINGER_PARTNER_URL = 'https://www.hostinger.com?REFERRALCODE=1JOHN0542';

export const HostingerPartnerBadge: React.FC<HostingerPartnerBadgeProps> = ({
  className = '',
  variant = 'inline',
}) => {
  if (variant === 'compact') {
    return (
      <a
        href={HOSTINGER_PARTNER_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 py-2 px-2 min-h-[44px] group transition-all transform hover:scale-[1.02] cursor-pointer ${className}`}
        title="Official Hostinger Partner - Domain & Cloud Hosting"
      >
        <img
          src="/Badge_brand_light.svg"
          alt="Official Hostinger Partner"
          className="h-7 w-auto object-contain drop-shadow-xs"
          loading="eager"
        />
      </a>
    );
  }

  if (variant === 'card') {
    return (
      <a
        href={HOSTINGER_PARTNER_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`group p-4 sm:p-5 min-h-[56px] rounded-2xl bg-gradient-to-br from-[#673DE6]/10 via-purple-500/5 to-white border border-[#673DE6]/30 hover:border-[#673DE6] shadow-xs hover:shadow-md transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 cursor-pointer ${className}`}
      >
        <div className="flex items-center gap-3.5">
          <img
            src="/Badge_brand_light.svg"
            alt="Official Hostinger Partner Badge"
            className="h-10 w-auto object-contain shrink-0 drop-shadow-xs"
          />
          <div>
            <div className="text-xs font-bold text-[#0B2D52] group-hover:text-[#673DE6] flex items-center gap-1.5 transition-colors">
              <span>Official Hostinger Partner</span>
              <ShieldCheck className="w-3.5 h-3.5 text-[#673DE6]" />
            </div>
            <p className="text-[11px] text-slate-600 leading-snug mt-0.5">
              High-performance NVMe cloud hosting, global CDN & domain registration for your AI website.
            </p>
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-1.5 text-xs font-bold text-[#673DE6] group-hover:translate-x-0.5 transition-transform">
          <span>Get Domain & Hosting</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </div>
      </a>
    );
  }

  if (variant === 'footer') {
    return (
      <a
        href={HOSTINGER_PARTNER_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`p-4 min-h-[56px] rounded-2xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700/80 hover:border-[#673DE6] transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 cursor-pointer ${className}`}
      >
        <div className="flex items-center gap-3">
          <img
            src="/Badge_brand_light.svg"
            alt="Hostinger Partner Badge"
            className="h-8 w-auto object-contain shrink-0"
          />
          <div>
            <div className="text-xs font-bold text-white flex items-center gap-1.5">
              <span>Official Infrastructure Partner</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            </div>
            <div className="text-[11px] text-slate-400">
              Hostinger Domain & High-Speed Cloud Hosting partner for Xweba clients.
            </div>
          </div>
        </div>

        <span className="text-xs font-bold text-[#DCD2FD] hover:text-white inline-flex items-center gap-1 bg-[#673DE6]/40 px-3.5 py-2 min-h-[40px] rounded-xl border border-[#673DE6]/60 transition-all">
          <span>Claim Partner Hosting</span>
          <ExternalLink className="w-3 h-3" />
        </span>
      </a>
    );
  }

  // Default: inline
  return (
    <a
      href={HOSTINGER_PARTNER_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2.5 px-3.5 py-2 min-h-[44px] rounded-xl bg-[#673DE6]/10 hover:bg-[#673DE6]/20 border border-[#673DE6]/30 transition-all cursor-pointer group ${className}`}
      title="Official Hostinger Partner"
    >
      <img
        src="/Badge_brand_light.svg"
        alt="Hostinger Partner"
        className="h-6 w-auto object-contain shrink-0"
      />
      <span className="text-[11px] font-bold text-[#0B2D52] group-hover:text-[#673DE6] flex items-center gap-1">
        <span>Domain & Hosting Partner</span>
        <ExternalLink className="w-3 h-3 text-[#673DE6]" />
      </span>
    </a>
  );
};
